import { Avatar, Box, Button, Chip, CircularProgress, Dialog, Fab, IconButton, List, ListItem, Paper, Stack, TextareaAutosize, Typography, Zoom, useMediaQuery, useTheme } from "@mui/material"
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import { useContext, useCallback, useEffect, useRef, useState } from "react";
import { getChat, getChatStream } from "src/services/chatbot.service";
import { MessageItem, BrodiThinkingLoader, TypingBubble, type ToolCallBadge } from "./Component";
import { SanitizedQuestion, TryoutContext } from "src/contexts/TryoutContext";
import { toInternalEcoursePath, toInternalProgramPath, type BrodiCompareItem, type BrodiMateriItem, type BrodiPriceData, type BrodiCtaData } from "src/utils/brodiButtonParser";
import { getChatbotPanelWidth } from "src/utils/chatbotLayout";

// Lebar maksimum panel chatbot di desktop (fallback untuk backward compat)
export const CHATBOT_SIDEBAR_WIDTH = 640;

// Helper: terjemahkan error menjadi pesan manusiawi untuk ditampilkan ke user
const mapErrorMessage = (error: unknown): string => {
  const message = error instanceof Error ? error?.message : String(error ?? "");
  const lower = message.toLowerCase();

  // Koneksi internet bermasalah
  if (
    lower.includes("failed to fetch") ||
    lower.includes("networkerror") ||
    lower.includes("load failed") ||
    lower.includes("network request failed") ||
    lower.includes("fetch failed")
  ) {
    return "Koneksi internet kamu bermasalah nih kak. Cek koneksinya, terus coba lagi ya.";
  }

  // Server sibuk / maintenance / timeout
  if (
    lower.includes("504") ||
    lower.includes("502") ||
    lower.includes("503") ||
    lower.includes("timeout") ||
    lower.includes("timed out") ||
    lower.includes("gateway")
  ) {
    return "Server Brodi lagi sibuk atau maintenance nih kak, coba lagi beberapa saat ya.";
  }

  // Sesi habis / tidak punya akses
  if (
    lower.includes("401") ||
    lower.includes("403") ||
    lower.includes("unauthorized") ||
    lower.includes("forbidden")
  ) {
    return "Sesi kamu sudah habis kak, coba login ulang dulu ya.";
  }

  // Pass-through: kalau pesan tidak mengandung kata teknis, biarkan pesan manusiawi
  // dari service layer lolos (contoh: "Server Brodi lagi sibuk nih kak, coba lagi dalam beberapa saat ya.")
  const technicalWords = [
    "failed to fetch",
    "networkerror",
    "load failed",
    "network request failed",
    "fetch failed",
    "http",
    "error",
    "undefined",
    "timeout",
    "gateway",
    "unauthorized",
    "forbidden",
  ];
  const containsTechnical = technicalWords.some((word) => lower.includes(word));

  if (!containsTechnical && message) {
    return message;
  }

  // Fallback umum
  return "Maaf kak, ada kendala teknis. Coba lagi dalam beberapa saat ya.";
};

const Index = ({ open, onToggle }: { open: boolean; onToggle: () => void }) => {
  const theme = useTheme();

  // Breakpoint detection menggunakan MUI useMediaQuery — Pure MUI Breakpoints
  // BREAKPOINT YANG BENAR: down('sm') → @media (max-width: 599.95px) → match < 600px
  // JANGAN pakai down('xs') — dengan xs:0 menghasilkan max-width:-0.05px yang TIDAK PERNAH match (MUI gotcha)
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));   // < 600px

  // Deteksi device: konsisten dengan pola existing di project (localStorage "isMobile")
  const isMobile = localStorage.getItem("isMobile");
  const isDesktop = isMobile === "false" || isMobile == null;

  // Mode Logic — 2 Mode:
  // - Mobile (< 600px): fullscreen Dialog
  // - Desktop (≥ 600px, termasuk tablet 600-959px): fixed overlay responsive

  // Detect "desktop site" di HP: Chrome desktop-site report innerWidth lebar (≥600) padahal
  // layar fisik kecil (screen.width < 600). Tanpa ini, panel desktop fixed right:0 lebar ~600px
  // ke-geser keluar di kanan layar HP → input kepotong/off-screen ("input tidak ada").
  const [isDesktopSiteOnMobile, setIsDesktopSiteOnMobile] = useState<boolean>(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const screenW = window.screen?.width ?? window.innerWidth;
    const innerW = window.innerWidth;
    // layar fisik HP (<600) tapi viewport report lebar (≥600) → desktop-site on mobile
    setIsDesktopSiteOnMobile(screenW < 600 && innerW >= 600);
  }, []);

  // Paksa mobile Dialog kalau desktop-site di HP, atau breakpoint < 600px
  const isMobileMode = isSmDown || isDesktopSiteOnMobile;
  const isDesktopMode = !isMobileMode;

  // Ref untuk lebar panel aktual (dipakai FAB positioning)
  const panelWidthRef = useRef<number>(640);

  // Hitung lebar panel dinamis berdasarkan viewport
  // Formula & breakpoint di-single-source-kan di src/utils/chatbotLayout (dipakai juga SidebarLayout untuk push content)
  const computePanelWidth = useCallback(() => {
    if (isMobileMode) return 0;  // fullscreen Dialog, width tidak dipakai

    const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
    return getChatbotPanelWidth(vw);
  }, [isMobileMode]);

  const panelWidth = computePanelWidth();
  panelWidthRef.current = panelWidth;

  const [messages, setMessages] = useState<{ text: string; sender: string; time: Date; tools?: ToolCallBadge[] }[]>([
    { text: "Halo! Ada yang bisa saya bantu?", sender: "bot", time: new Date() }
  ]);
  const [input, setInput] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string>("");
  const [typingText, setTypingText] = useState<string | null>(null);
  const [isBotResponding, setIsBotResponding] = useState<boolean>(false);
  const [streamTools, setStreamTools] = useState<ToolCallBadge[]>([]);
  const [activeContext, setActiveContext] = useState<SanitizedQuestion | null>(null);
  const [manualCleared, setManualCleared] = useState<boolean>(false);
  const { currentQuestion } = useContext(TryoutContext);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingRef = useRef<HTMLLIElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const shouldAutoScroll = useRef(true);
  // Refs untuk streaming
  const streamControllerRef = useRef<AbortController | null>(null);
  const isStreamingRef = useRef(false);
  // Ref untuk accumulate text (fix closure stale di onComplete)
  const accumulatedTextRef = useRef<string>("");
  // Ref tools untuk onComplete (hindari stale state)
  const streamToolsRef = useRef<ToolCallBadge[]>([]);

  // Fungsi untuk scroll ke bawah
  const scrollToBottom = useRef<(behavior?: ScrollBehavior) => void>((behavior = 'smooth') => {
    const container = chatContainerRef.current;
    if (!container) return;

    setTimeout(() => {
      if (typingText && typingRef.current) {
        typingRef.current.scrollIntoView({
          behavior,
          block: 'nearest'
        });
      } else if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior,
          block: 'end'
        });
      }
    }, 50);
  }).current;

  // Fungsi untuk scroll ke pesan bot terakhir
  const scrollToLastBotMessage = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
  };

  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;

      // Jika user scroll ke atas, jangan ganggu
      if (!isNearBottom) {
        shouldAutoScroll.current = false;
      } else {
        shouldAutoScroll.current = true;
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const legacySession = localStorage.getItem("chat_session_id");

    if (legacySession) {
      setMessages(prev => [
        ...prev,
        {
          text: "Catatan: Riwayat chat sebelumnya tidak tersedia setelah pembaruan ini",
          sender: "bot",
          time: new Date()
        }
      ]);
    }

    localStorage.removeItem("chat_session_id");
    const newSessionId = "session-" + Math.random().toString(36).substring(2, 9);
    setSessionId(newSessionId);
  }, []);

  useEffect(() => {
    // Scroll ketika ada perubahan messages, typingText, tools
    if (typingText || streamTools.length > 0) {
      scrollToBottom();
    } else if (messages.length > 0) {
      scrollToLastBotMessage();
    }
  }, [messages, typingText, streamTools]);

  useEffect(() => {
    if (shouldAutoScroll.current) {
      scrollToBottom('auto');
    }
  }, [messages, typingText, isBotResponding, streamTools]);

  // reset manual clear when question changes so new question can be auto-selected
  useEffect(() => {
    setManualCleared(false);
  }, [currentQuestion]);

  // ketika chat dibuka, auto-set konteks ke currentQuestion jika ada dan belum ada activeContext
  useEffect(() => {
    if (open && currentQuestion && !activeContext && !manualCleared) {
      setActiveContext(currentQuestion);
    }
    // only run when open or currentQuestion changes
  }, [open, currentQuestion, activeContext]);

  // Abort stream saat chatbot ditutup (open berubah jadi false)
  useEffect(() => {
    if (!open && streamControllerRef.current) {
      streamControllerRef.current.abort();
      streamControllerRef.current = null;
      isStreamingRef.current = false;
      setTypingText(null);
      setIsBotResponding(false);
      setLoading(false);
      setStreamTools([]);
      streamToolsRef.current = [];
    }
  }, [open]);

  // Core function to send a message (used by both form submit and button clicks)
  const sendMessage = useCallback(
    async (message: string, options?: { isButtonClick?: boolean; skipContext?: boolean }) => {
      // Abort stream sebelumnya jika ada
      if (streamControllerRef.current) {
        streamControllerRef.current.abort();
        streamControllerRef.current = null;
      }
      isStreamingRef.current = true;

      const userMessage = { text: message, sender: "user", time: new Date() };
      setMessages((prev) => [...prev, userMessage]);
      setInput("");
      setLoading(true);
      // SSE: tampilkan "thinking" hanya sampai token konten pertama atau tool call
      setIsBotResponding(true);
      setTypingText(null);
      setStreamTools([]);
      streamToolsRef.current = [];
      shouldAutoScroll.current = true;

      // Reset accumulated text ref
      accumulatedTextRef.current = "";

      let finalPrompt = message;

      // HANYA JIKA ADA KONTEKS YANG 'TERKUNCI' AKTIF dan bukan button click
      if (activeContext && !options?.skipContext) {
        const contextPrompt = `
          Sebagai tutor AI, jawab pertanyaan pengguna berdasarkan konteks soal try out berikut. Jangan menjawab di luar konteks ini.
          Konteks Soal:
          - Pertanyaan: "${activeContext?.question}"
          - Pilihan: ${activeContext?.choices?.join(", ")}
          - Pembahasan: "${activeContext?.explanation}"

          Pertanyaan Pengguna: "${message}"

          Jawab pertanyaan pengguna berdasarkan konteks soal di atas.
        `;
        finalPrompt = contextPrompt;

        // SANGAT PENTING: Reset konteks setelah digunakan sekali!
        // setActiveContext(null);
      }

      // Buat AbortController untuk stream ini
      const controller = new AbortController();
      streamControllerRef.current = controller;

      try {
        await getChatStream(
          { session_id: sessionId, message: finalPrompt },
          // onChunk: token SSE diterima
          (token: string) => {
            if (!isStreamingRef.current) return; // Sudah di-abort
            // Skip leading whitespace: token whitespace murni sebelum ada konten → jangan render,
            // dan jangan akumulasi — cegah bubble kosong saat response baru mulai
            if (!accumulatedTextRef.current.trim() && token.trim() === "") return;

            const isFirstContentToken = !accumulatedTextRef.current.trim();
            if (isFirstContentToken) {
              // Token konten pertama: tutup thinking loader, mulai bubble streaming
              setIsBotResponding(false);
            }

            accumulatedTextRef.current += token;
            setTypingText((prev) => (prev || "") + token);
          },
          // onComplete: stream selesai
          () => {
            if (!isStreamingRef.current) return;
            const toolsSnapshot = streamToolsRef.current.length > 0 ? [...streamToolsRef.current] : undefined;
            // Guard: tanpa konten ter-akumulasi (stream error / hanya whitespace) → jangan push message kosong
            if (!accumulatedTextRef.current.trim()) {
              setIsBotResponding(false);
              setTypingText(null);
              setStreamTools([]);
              streamToolsRef.current = [];
              setLoading(false);
              isStreamingRef.current = false;
              streamControllerRef.current = null;
              return;
            }
            setMessages((prev) => [
              ...prev,
              {
                text: accumulatedTextRef.current || "",
                sender: "bot",
                time: new Date(),
                tools: toolsSnapshot,
              },
            ]);
            setIsBotResponding(false);
            setTypingText(null);
            setStreamTools([]);
            streamToolsRef.current = [];
            setLoading(false);
            isStreamingRef.current = false;
            streamControllerRef.current = null;
            scrollToBottom("smooth");
          },
          // onError: error handling
          (error: Error) => {
            if (!isStreamingRef.current) return;
            // Log untuk debugging developer, jangan tampilkan ke user
            console.error("Chatbot stream error:", error);
            setIsBotResponding(false);
            const toolsSnapshot = streamToolsRef.current.length > 0 ? [...streamToolsRef.current] : undefined;

            // Kalau sudah ada partial text yang dirender → push dulu sebagai message bot,
            // LALU push pesan error. Tanpa partial → push pesan error saja.
            const partialText = accumulatedTextRef.current?.trim();
            if (partialText) {
              setMessages((prev) => [
                ...prev,
                { text: accumulatedTextRef.current, sender: "bot", time: new Date(), tools: toolsSnapshot },
                { text: mapErrorMessage(error), sender: "bot", time: new Date() },
              ]);
            } else {
              setMessages((prev) => [
                ...prev,
                {
                  text: mapErrorMessage(error),
                  sender: "bot",
                  time: new Date(),
                },
              ]);
            }

            // Reset accumulated ref — cegah closure stale untuk stream berikutnya
            accumulatedTextRef.current = "";

            setTypingText(null);
            setStreamTools([]);
            streamToolsRef.current = [];
            setLoading(false);
            isStreamingRef.current = false;
            streamControllerRef.current = null;
            scrollToBottom("smooth");
          },
          // Pass AbortSignal ke fetch
          controller.signal,
          // ToolCallStarted / ToolCallCompleted
          (toolEvent) => {
            if (!isStreamingRef.current) return;

            setStreamTools((prev) => {
              const next = [...prev];
              const idx = next.findIndex((t) => t.tool_call_id === toolEvent.tool_call_id);

              if (toolEvent.type === "started") {
                const badge: ToolCallBadge = {
                  tool_call_id: toolEvent.tool_call_id,
                  tool_name: toolEvent.tool_name,
                  status: "running",
                };
                if (idx >= 0) {
                  next[idx] = badge;
                } else {
                  next.push(badge);
                }
              } else {
                // completed
                const badge: ToolCallBadge = {
                  tool_call_id: toolEvent.tool_call_id,
                  tool_name: toolEvent.tool_name,
                  status: toolEvent.success === false ? "error" : "done",
                  success: toolEvent.success,
                };
                if (idx >= 0) {
                  next[idx] = { ...next[idx], ...badge };
                } else {
                  next.push(badge);
                }
              }

              streamToolsRef.current = next;
              return next;
            });
          }
        );
      } catch (error) {
        // Error synchronously thrown (sebelum stream mulai)
        if (!isStreamingRef.current) return;
        // Log untuk debugging developer, jangan tampilkan ke user
        console.error("Chatbot submit error:", error);
        setIsBotResponding(false);
        setMessages((prev) => [
          ...prev,
          {
            text: mapErrorMessage(error),
            sender: "bot",
            time: new Date(),
          },
        ]);
        setTypingText(null);
        setStreamTools([]);
        streamToolsRef.current = [];
        setLoading(false);
        isStreamingRef.current = false;
        streamControllerRef.current = null;
        scrollToBottom("smooth");
      }
    },
    [activeContext, sessionId]
  );

  // Handler: retry tampilkan helper yang gagal parse
  const handleHelperRetry = useCallback(() => {
    sendMessage(
      "Komponen sebelumnya gagal ditampilkan. Tolong tampilkan ulang konten/rekomendasinya dengan format yang benar ya.",
      { isButtonClick: true, skipContext: true }
    );
  }, [sendMessage]);

  // Handler untuk klik button BRODI_BUTTON
  const handleBrodiButtonClick = useCallback(
    (title: string) => {
      // Button clicks skip context injection - they're direct responses
      sendMessage(title, { isButtonClick: true, skipContext: true });
    },
    [sendMessage]
  );

  // Handler untuk klik kartu materi BRODI_MATERI: buka materi di tab baru
  const handleBrodiMateriClick = useCallback(
    (item: BrodiMateriItem) => {
      const path = toInternalEcoursePath(item?.url);
      if (path) {
        window.open(path, "_blank", "noopener,noreferrer");
      } else if (item?.url) {
        // Fallback: buka URL asli dari payload kalau bukan route internal yang dikenal
        window.open(item.url, "_blank", "noopener,noreferrer");
      }
      // URL kosong → diam saja, jangan crash
    },
    []
  );

  // Handler untuk klik kartu paket BRODI_COMPARE: buka halaman program di tab baru
  const handleBrodiCompareClick = useCallback(
    (item: BrodiCompareItem, programId?: number, programUrl?: string, program?: { id?: number; title?: string; image?: string }) => {
      // Prioritas: URL item → URL level compare (di luar items) → fallback path internal dari program_id atau nested program.id
      const path = toInternalProgramPath(item?.url);
      if (path) {
        window.open(path, "_blank", "noopener,noreferrer");
      } else {
        const comparePath = toInternalProgramPath(programUrl);
        if (comparePath) {
          window.open(comparePath, "_blank", "noopener,noreferrer");
        } else if (programId) {
          const fallbackPath = toInternalProgramPath(`/program/${programId}`);
          if (fallbackPath) {
            window.open(fallbackPath, "_blank", "noopener,noreferrer");
          }
        } else if (program?.id) {
          // Fallback dari nested program object (new structure)
          const fallbackPath = toInternalProgramPath(`/program/${program.id}`);
          if (fallbackPath) {
            window.open(fallbackPath, "_blank", "noopener,noreferrer");
          }
        }
      }
      // URL tidak valid → diam saja, jangan crash
    },
    []
  );

  // Handler untuk klik kartu harga BRODI_PRICE: buka halaman program di tab baru
  const handleBrodiPriceClick = useCallback(
    (data: BrodiPriceData) => {
      // Prioritas: URL payload → fallback path internal dari program_id atau nested program.id
      const path = toInternalProgramPath(data?.url);
      if (path) {
        window.open(path, "_blank", "noopener,noreferrer");
      } else if (data?.program_id) {
        const fallbackPath = toInternalProgramPath(`/program/${data.program_id}`);
        if (fallbackPath) {
          window.open(fallbackPath, "_blank", "noopener,noreferrer");
        }
      } else if (data?.program?.id) {
        // Fallback dari nested program object (new structure)
        const fallbackPath = toInternalProgramPath(`/program/${data.program.id}`);
        if (fallbackPath) {
          window.open(fallbackPath, "_blank", "noopener,noreferrer");
        }
      }
      // URL & program_id tidak valid → diam saja, jangan buka URL prod mentah di dev (konsisten materi/compare)
    },
    []
  );

  // Handler untuk klik kartu CTA BRODI_CTA: buka halaman program di tab baru
  const handleBrodiCtaClick = useCallback(
    (data: BrodiCtaData) => {
      // Prioritas: URL payload → fallback path internal dari program_id
      const path = toInternalProgramPath(data?.url);
      if (path) {
        window.open(path, "_blank", "noopener,noreferrer");
      } else if (data?.program_id) {
        const fallbackPath = toInternalProgramPath(`/program/${data.program_id}`);
        if (fallbackPath) {
          window.open(fallbackPath, "_blank", "noopener,noreferrer");
        }
      }
      // URL & program_id tidak valid → diam saja, jangan buka URL prod mentah di dev (konsisten materi/compare/price)
    },
    []
  );

  // Bug 2a: Stabilkan TextareaAutosize onChange dengan useCallback
  // Cegah re-render berantai saat ketik di mobile
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    await sendMessage(input);
  };

  // Konten bersama: dipakai desktop sidebar & mobile fullscreen
  const botAvatarSx = {
    width: 28,
    height: 28,
    bgcolor: "background.paper",
    border: "1px solid",
    borderColor: "divider",
    mr: 1,
    mt: 0.25,
    flexShrink: 0,
  } as const;

  const chatHeader = (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider",
        px: 1.5,
        py: 1,
        minHeight: 52,
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexShrink: 0,
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <img src="/android-chrome-192x192.png" alt="Brodi" width={24} height={24} style={{ objectFit: "contain" }} />
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
          Brodi AI
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: "success.main",
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            Online
          </Typography>
        </Stack>
      </Box>
      <IconButton
        onClick={onToggle}
        aria-label="Tutup chat"
        size="small"
        sx={{
          color: "text.secondary",
          cursor: "pointer",
          transition: "background-color 0.2s ease, color 0.2s ease",
          "&:hover": { color: "text.primary", bgcolor: "action.hover" },
          "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const chatBody = (
    <Box
      ref={chatContainerRef}
      sx={{
        overflowY: "auto",
        overflowX: "hidden",
        flex: 1,
        minHeight: 0,
        bgcolor: "background.default",
        px: 0.5,
        "&::-webkit-scrollbar": {
          width: "0.35em",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(0,0,0,.12)",
          borderRadius: "10px",
        },
      }}
    >
      <List disablePadding sx={{ py: 1, px: 0.5 }}>
        {messages.map((msg, index) => (
          <MessageItem key={`msg-${index}`} message={msg} onButtonClick={handleBrodiButtonClick} onMateriClick={handleBrodiMateriClick} onCompareClick={handleBrodiCompareClick} onPriceClick={handleBrodiPriceClick} onCtaClick={handleBrodiCtaClick} onHelperRetry={handleHelperRetry} />
        ))}

        {isBotResponding && !typingText && streamTools.length === 0 && (
          <ListItem
            sx={{
              justifyContent: "flex-start",
              alignItems: "flex-start",
              py: 0.75,
              px: 1,
            }}
          >
            <Avatar sx={botAvatarSx}>
              <img src="/android-chrome-192x192.png" alt="Brodi" width={20} height={20} style={{ objectFit: "contain" }} />
            </Avatar>
            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: "action.hover",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "16px 16px 16px 4px",
                boxShadow: "none",
              }}
            >
              <BrodiThinkingLoader />
            </Paper>
          </ListItem>
        )}

        {(typingText || streamTools.length > 0) && (
          <ListItem
            ref={typingRef}
            sx={{
              justifyContent: "flex-start",
              alignItems: "flex-start",
              transition: "all 0.2s ease",
              py: 0.75,
              px: 1,
            }}
          >
            <Avatar sx={botAvatarSx}>
              <img src="/android-chrome-192x192.png" alt="Brodi" width={20} height={20} style={{ objectFit: "contain" }} />
            </Avatar>
            <TypingBubble
              text={typingText}
              tools={streamTools}
              showThinking={isBotResponding && !typingText}
              onButtonClick={handleBrodiButtonClick}
              onCompareClick={handleBrodiCompareClick}
              onPriceClick={handleBrodiPriceClick}
              onCtaClick={handleBrodiCtaClick}
              onHelperRetry={handleHelperRetry}
            />
          </ListItem>
        )}

        <div ref={messagesEndRef} />
      </List>
    </Box>
  );

  const chatInput = (
    <Box
      sx={{
        px: 1.5,
        pt: 1,
        pb: 1.5,
        bgcolor: "background.paper",
        borderTop: "1px solid",
        borderColor: "divider",
        position: "sticky",
        bottom: 0,
        flexShrink: 0,
      }}
    >
      {currentQuestion && !activeContext && (
        <Button
          size="small"
          variant="outlined"
          onClick={() => setActiveContext(currentQuestion)}
          sx={{
            mb: 1,
            textTransform: "none",
            width: "100%",
            borderRadius: 2,
            fontWeight: 500,
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          Tanyakan tentang Soal {currentQuestion.questionNumber} saat ini
        </Button>
      )}

      {activeContext && (
        <Chip
          size="small"
          label={`Konteks: Soal ${activeContext.questionNumber}`}
          onDelete={() => {
            setActiveContext(null);
            setManualCleared(true); // prevent immediate auto-reselect
          }}
          color="primary"
          variant="outlined"
          sx={{ mb: 1, fontWeight: 500 }}
        />
      )}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 0.75,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          bgcolor: "background.default",
          px: 1,
          py: 0.5,
          transition: "border-color 0.2s ease",
          "&:focus-within": {
            borderColor: "primary.main",
          },
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            "& textarea": {
              width: "100%",
              resize: "none",
              border: "none",
              outline: "none",
              background: "transparent",
              fontFamily: "inherit",
              fontSize: "0.9375rem",
              lineHeight: 1.45,
              color: "inherit",
              padding: "6px 4px",
              "&:disabled": {
                opacity: 0.6,
              },
            },
          }}
        >
          <TextareaAutosize
            minRows={1}
            maxRows={6}
            value={input}
            onChange={handleInputChange}
            placeholder="Tanya Brodi..."
            disabled={loading}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                handleSubmit(e);
              }
            }}
          />
        </Box>
        <IconButton
          type="submit"
          color="primary"
          disabled={!input.trim() || loading}
          size="small"
          aria-label="Kirim pesan"
          sx={{
            mb: 0.25,
            cursor: "pointer",
            transition: "background-color 0.2s ease, opacity 0.2s ease",
            bgcolor: !input.trim() || loading ? "transparent" : "primary.main",
            color: !input.trim() || loading ? "text.disabled" : "primary.contrastText",
            "&:hover": {
              bgcolor: !input.trim() || loading ? "action.hover" : "primary.dark",
            },
            "&.Mui-disabled": {
              bgcolor: "transparent",
              color: "text.disabled",
            },
            "&:focus-visible": {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: 2,
            },
          }}
        >
          {loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Box>
      <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.75, px: 0.25, fontSize: "0.6875rem" }}>
        Brodi adalah AI, dan AI dapat membuat kesalahan, pastikan untuk memeriksa kembali jawaban yang diberikan.
      </Typography>
    </Box>
  );

  return (
    <>
      {/* Fab toggle: desktop selalu tampil (zIndex di atas panel), mobile tampil saat chatbot tertutup */}
      <Zoom in={isDesktopMode ? true : !open} style={{ transitionDelay: "500ms" }}>
        <Fab
          aria-label={open ? "Tutup Brodi AI" : "Buka Brodi AI"}
          onClick={() => {
            onToggle();
            // immediate attempt to set context (in case useEffect timing varies)
            if (currentQuestion && !activeContext) setActiveContext(currentQuestion);
          }}
          sx={(theme) => {
            const g1 = theme.palette.primary.light;
            const g2 = theme.palette.primary.main;
            const g3 = theme.palette.primary.dark;
            return {
              position: "fixed",
              // FAB position dinamis mengikuti lebar panel aktual
              right: isDesktopMode
                ? (open ? panelWidthRef.current + 24 : 24)
                : { xs: 30, sm: 24, md: 32 },
              bottom: { xs: 80, lg: 25 },
              width: { xs: 54, lg: 60 },
              height: { xs: 54, lg: 60 },
              p: 0.75,
              zIndex: 1300,
              overflow: "visible",
              cursor: "pointer",
              color: theme.palette.primary.contrastText,
              // Gradient primary brand (bukan ungu generik)
              background: `linear-gradient(135deg, ${g1} 0%, ${g2} 48%, ${g3} 100%)`,
              backgroundSize: "200% 200%",
              boxShadow: `0 6px 20px ${theme.palette.primary.main}55, 0 2px 8px rgba(0,0,0,0.12)`,
              transition: theme.transitions.create(
                ["right", "transform", "box-shadow", "background-position"],
                { duration: theme.transitions.duration.shorter }
              ),
              animation: open
                ? "brodiFabGradient 6s ease infinite"
                : "brodiFabGradient 6s ease infinite, brodiFabPulse 2.4s ease-in-out infinite",
              "@keyframes brodiFabGradient": {
                "0%": { backgroundPosition: "0% 50%" },
                "50%": { backgroundPosition: "100% 50%" },
                "100%": { backgroundPosition: "0% 50%" },
              },
              "@keyframes brodiFabPulse": {
                "0%, 100%": {
                  boxShadow: `0 6px 20px ${theme.palette.primary.main}55, 0 2px 8px rgba(0,0,0,0.12)`,
                },
                "50%": {
                  boxShadow: `0 8px 28px ${theme.palette.primary.main}80, 0 0 0 10px ${theme.palette.primary.main}18`,
                },
              },
              "@keyframes brodiFabRing": {
                "0%": { transform: "scale(1)", opacity: 0.55 },
                "70%": { transform: "scale(1.45)", opacity: 0 },
                "100%": { transform: "scale(1.45)", opacity: 0 },
              },
              // Ring ambient di belakang tombol saat panel tertutup
              "&::before": open
                ? { display: "none" }
                : {
                  content: '""',
                  position: "absolute",
                  inset: -3,
                  borderRadius: "50%",
                  border: `2px solid ${theme.palette.primary.light}`,
                  animation: "brodiFabRing 2.4s ease-out infinite",
                  pointerEvents: "none",
                  zIndex: -1,
                },
              "&:hover": {
                background: `linear-gradient(135deg, ${g1} 10%, ${g2} 55%, ${g3} 100%)`,
                boxShadow: `0 10px 28px ${theme.palette.primary.main}70, 0 4px 12px rgba(0,0,0,0.14)`,
                transform: "scale(1.08)",
                animation: "brodiFabGradient 4s ease infinite",
              },
              "&:active": {
                transform: "scale(0.96)",
              },
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: theme.palette.primary.main,
                outlineOffset: 3,
              },
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
                backgroundSize: "100% 100%",
                transition: theme.transitions.create("right"),
                "&::before": { animation: "none", display: "none" },
                "&:hover": {
                  animation: "none",
                  transform: "none",
                },
              },
            };
          }}
        >
          <Box
            component="img"
            src="/ai.png"
            alt="Brodi AI"
            onError={(e) => {
              const target = e.currentTarget as HTMLImageElement;
              // Fallback ke default bila gambar error/404 — cegah loop kalau default ikut error
              if (!target.src.endsWith("/android-chrome-512x512.png")) {
                target.src = "/android-chrome-512x512.png";
              }
            }}
            sx={{
              width: "82%",
              height: "82%",
              objectFit: "contain",
              display: "block",
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.15))",
              animation: open ? "none" : "brodiFabIconFloat 3s ease-in-out infinite",
              "@keyframes brodiFabIconFloat": {
                "0%, 100%": { transform: "translateY(0)" },
                "50%": { transform: "translateY(-2px)" },
              },
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
              },
            }}
          />
        </Fab>
      </Zoom>

      {/* Desktop (≥ 600px, termasuk tablet): fixed overlay panel responsive — hanya dirender di desktop mode */}
      {isDesktopMode && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            height: "100vh",
            width: panelWidth,
            minWidth: panelWidth,
            maxWidth: panelWidth,
            zIndex: 1200,
            bgcolor: "background.paper",
            boxShadow: (theme) => theme.shadows[10],
            transform: open ? "translateX(0)" : "translateX(100%)",
            transition: (theme) =>
              theme.transitions.create("transform", {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.standard,
              }),
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {chatHeader}
          {chatBody}
          {chatInput}
        </Box>
      )}

      {/* Mobile (< 600px): fullscreen dialog chatbot — hanya dirender di mobile mode (MUI Modal lock body scroll saat open) */}
      {isMobileMode && (
        <Dialog
          fullScreen
          open={open}
          onClose={onToggle}
          disableEnforceFocus
          PaperProps={{
            // Bug 1 + Bug 3: tinggi keyboard-aware. Pakai PaperProps (bukan sx Dialog) biar
            // menang atas MUI fullScreen default height:100%. Saat visualViewport tersedia:
            // height = min(vv.height, innerHeight - vv.pageTop) → input tetap di atas keyboard
            // (iOS & Android). Fallback 100dvh (exclude URL bar) saat listener belum jalan.
            sx: {
              display: "flex",
              flexDirection: "column",
              height: "100dvh",
              maxHeight: "100dvh",
              overflow: "hidden",
              // Bug 2b: Cegah panel melebar saat ketik panjang (maxRows=6)
              width: "100%",
              maxWidth: "100vw",
              overflowX: "hidden",
            },
          }}
        >
          {chatHeader}
          {chatBody}
          {chatInput}
        </Dialog>
      )}
    </>
  )
}

export default Index
